import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/cartSlice';
import { translations } from '../utils/translations';
import AboutHero from './AboutHero';
import { trackEvent } from '../utils/analytics';
import './ContactUs.css';

const ContactUs = () => {
  const selectedLang = useSelector(selectLanguage) || { code: 'en' };
  const t = translations[selectedLang.code || 'en'] || translations.en;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phonePrefix: '+31',
    phone: '',
    message: ''
  });

  useEffect(() => {
    document.title = `${t.contactUs} | Smart Telecom Store`;

    let metaDescription = document.querySelector('meta[name="description"]');
    const descriptionText = t.contactDesc || 'Contact Smart Telecom Store for inquiries, product questions, order updates, or support. Send us a message or reach us via WhatsApp, phone, or email.';

    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      document.head.appendChild(metaDescription);
    }

    const originalContent = metaDescription.content;
    metaDescription.content = descriptionText;

    return () => {
      document.title = 'Smart Telecom Store | Premium Electronics Store';
      if (metaDescription) {
        metaDescription.content = originalContent || '';
      }
    };
  }, [t]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getAlertMessage = (firstName) => {
    switch (selectedLang.code) {
      case 'nl': return `Bedankt ${firstName}! Uw bericht is verzonden naar Smart Telecom Store.`;
      case 'es': return `¡Gracias ${firstName}! Tu mensaje ha sido enviado a Smart Telecom Store.`;
      case 'fr': return `Merci ${firstName} ! Votre message a été envoyé à Smart Telecom Store.`;
      case 'de': return `Vielen Dank ${firstName}! Ihre Nachricht wurde an den Smart Telecom Store gesendet.`;
      case 'it': return `Grazie ${firstName}! Il tuo messaggio è stato inviato a Smart Telecom Store.`;
      case 'pt': return `Obrigado ${firstName}! Sua mensagem foi enviada para a Smart Telecom Store.`;
      case 'el': return `Ευχαριστούμε ${firstName}! Το μήνυμά σας στάλθηκε στο Smart Telecom Store.`;
      default: return `Thank you ${firstName}! Your message has been sent to Smart Telecom Store.`;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('contact_form_submit', { form: 'contact_us' });
    alert(getAlertMessage(formData.firstName));
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phonePrefix: '+31',
      phone: '',
      message: ''
    });
  };

  const ct = {
    sendUsMessage: {
      en: "Send us a message",
      nl: "Stuur ons een bericht",
      es: "Envíenos un mensaje",
      fr: "Envoyez-nous un message",
      de: "Schicken Sie uns eine Nachricht",
      it: "Inviaci un messaggio",
      pt: "Envie-nos uma mensagem",
      el: "Στείλτε μας ένα μήνυμα"
    },
    contactSubtitle: {
      en: "Do you have a question? A complaint? Or need any help to choose the right product from Smart Telecom Store. Feel free to contact us.",
      nl: "Heeft u een vraag? Een klacht? Of heeft u hulp nodig om het juiste product te kiezen van Smart Telecom Store. Neem gerust contact met ons op.",
      es: "¿Tiene una pregunta? ¿Una queja? O necesita ayuda para elegir el producto adecuado de Smart Telecom Store. No dude en contactarnos.",
      fr: "Avez-vous une question ? Une plainte ? Ou avez-vous besoin d'aide pour choisir le bon produit chez Smart Telecom Store. N'hésitez pas à nous contacter.",
      de: "Haben Sie eine Frage? Eine Beschwerde? Oder benötigen Sie Hilfe bei der Auswahl des richtigen Produkts aus dem Smart Telecom Store. Kontaktieren Sie uns gerne.",
      it: "Hai una domanda? Un reclamo? O hai bisogno di aiuto per scegliere il prodotto giusto da Smart Telecom Store. Sentiti libero di contattarci.",
      pt: "Tem alguma dúvida? Uma reclamação? Ou precisa de ajuda para escolher o produto certo na Smart Telecom Store. Sinta-se à vontade para entrar em contato conosco.",
      el: "Έχετε κάποια ερώτηση; Ένα παράπονο; Ή χρειάζεστε βοήθεια για να επιλέξετε το κατάλληλο προϊόν από το Smart Telecom Store. Μη διστάσετε να επικοινωνήσετε μαζί μας."
    },
    firstName: {
      en: "First Name",
      nl: "Voornaam",
      es: "Nombre",
      fr: "Prénom",
      de: "Vorname",
      it: "Nome",
      pt: "Primeiro Nome",
      el: "Όνομα"
    },
    lastName: {
      en: "Last Name",
      nl: "Achternaam",
      es: "Apellido",
      fr: "Nom",
      de: "Nachname",
      it: "Cognome",
      pt: "Sobrenome",
      el: "Επώνυμο"
    },
    placeholderFirstName: {
      en: "Enter your first name",
      nl: "Vul uw voornaam in",
      es: "Introduce tu nombre",
      fr: "Entrez votre prénom",
      de: "Vorname eingeben",
      it: "Inserisci il tuo nome",
      pt: "Digite seu primeiro nome",
      el: "Εισάγετε το όνομά σας"
    },
    placeholderLastName: {
      en: "Enter your last name",
      nl: "Vul uw achternaam in",
      es: "Introduce tu apellido",
      fr: "Entrez votre nom",
      de: "Nachname eingeben",
      it: "Inserisci il tuo cognome",
      pt: "Digite seu sobrenome",
      el: "Εισάγετε το επώνυμό σας"
    },
    contactDetails: {
      en: "Contact Details",
      nl: "Contactgegevens",
      es: "Detalles de Contacto",
      fr: "Coordonnées",
      de: "Kontaktdaten",
      it: "Dettagli di Contatto",
      pt: "Detalhes de Contato",
      el: "Στοιχεία Επικοινωνίας"
    },
    placeholderPhone: {
      en: "Enter your contact number",
      nl: "Vul uw telefoonnummer in",
      es: "Introduce tu número de contacto",
      fr: "Entrez votre numéro de téléphone",
      de: "Telefonnummer eingeben",
      it: "Inserisci il tuo numero di telefono",
      pt: "Digite seu número de contato",
      el: "Εισάγετε τον αριθμό επικοινωνίας σας"
    },
    placeholderMessage: {
      en: "Enter your message",
      nl: "Vul uw bericht in",
      es: "Introduce tu mensaje",
      fr: "Entrez votre message",
      de: "Nachricht eingeben",
      it: "Inserisci il tuo messaggio",
      pt: "Digite sua mensagem",
      el: "Εισάγετε το μήνυμά σας"
    },
    sendBtn: {
      en: "Send a Message",
      nl: "Bericht Verzenden",
      es: "Enviar un Mensaje",
      fr: "Envoyer un Message",
      de: "Nachricht senden",
      it: "Invia un Messaggio",
      pt: "Enviar uma Mensagem",
      el: "Αποστολή Μηνύματος"
    },
    sidebarTitle: {
      en: "Hi! We are always here to help you.",
      nl: "Hallo! We staan altijd voor u klaar.",
      es: "¡Hola! Siempre estamos aquí para ayudarte.",
      fr: "Salut ! Nous sommes toujours là pour vous aider.",
      de: "Hallo! Wir sind immer für Sie da.",
      it: "Ciao! Siamo sempre qui per aiutarti.",
      pt: "Olá! Estamos sempre aqui para ajudá-lo.",
      el: "Γεια σας! Είμαστε πάντα εδώ για να σας βοηθήσουμε."
    },
    sidebarDesc: {
      en: "Have any inquiries or need support with your device? Fill out the form or reach out directly through our hotline, WhatsApp, or email.",
      nl: "Heeft u vragen of heeft u ondersteuning nodig met uw apparaat? Vul het formulier in of neem rechtstreeks contact op via onze hotline, WhatsApp of e-mail.",
      es: "¿Tiene alguna consulta o necesita asistencia con su dispositivo? Complete el formulario o comuníquese directamente a través de nuestra línea directa, WhatsApp o correo electrónico.",
      fr: "Vous avez des questions ou besoin d'assistance pour votre appareil ? Remplissez le formulaire ou contactez-nous directement par téléphone, WhatsApp ou e-mail.",
      de: "Haben Sie Fragen oder benötigen Sie Unterstützung bei Ihrem Gerät? Füllen Sie das Formular aus oder wenden Sie sich direkt an unsere Hotline, WhatsApp oder E-Mail.",
      it: "Hai domande o hai bisogno di supporto per il tuo dispositivo? Compila il modulo o contattaci direttamente tramite la nostra hotline, WhatsApp o e-mail.",
      pt: "Tem alguma dúvida ou precisa de suporte com seu dispositivo? Preencha o formulário ou entre em contato diretamente pelo nosso atendimento, WhatsApp ou e-mail.",
      el: "Έχετε απορίες ή χρειάζεστε υποστήριξη για τη συσκευή σας; Συμπληρώστε τη φόρμα ή επικοινωνήστε απευθείας μέσω της τηλεφωνικής μας γραμμής, του WhatsApp ή του email."
    },
    connectWithUs: {
      en: "Connect with us",
      nl: "Volg ons",
      es: "Conéctate con nosotros",
      fr: "Rejoignez-nous",
      de: "Verbinden Sie sich mit uns",
      it: "Connettiti con noi",
      pt: "Conecte-se conosco",
      el: "Συνδεθείτε μαζί μας"
    }
  };

  const getLocText = (key) => {
    return ct[key]?.[selectedLang.code] || ct[key]?.en || "";
  };

  return (
    <div className="contact-us-page">
      {/* 1. Header Banner */}
      <AboutHero />

      {/* 2. Contact form layout */}
      <section className="contact-form-section">
        <div className="contact-form-inner">
          <div className="contact-grid-container">

            {/* Left Column: Form & FAQ container */}
            <div className="contact-left-form">
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
                <h2 className="form-main-title">{getLocText('sendUsMessage')}</h2>
                <p className="form-subtitle">
                  {getLocText('contactSubtitle')}
                </p>

                <div className="form-row">
                  <div className="form-field">
                    <label className="field-label">{getLocText('firstName')}</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      placeholder={getLocText('placeholderFirstName')}
                      className="field-input"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">{getLocText('lastName')}</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      placeholder={getLocText('placeholderLastName')}
                      className="field-input"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-field">
                    <label className="field-label">{t.emailAddress}</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t.emailPlaceholder || "Enter your email"}
                      className="field-input"
                      required
                    />
                  </div>
                  <div className="form-field">
                    <label className="field-label">{getLocText('contactDetails')}</label>
                    <div className="phone-input-group">
                      <select
                        name="phonePrefix"
                        value={formData.phonePrefix}
                        onChange={handleChange}
                        className="prefix-select"
                      >
                        <option value="+31">+31</option>
                        <option value="+44">+44</option>
                        <option value="+30">+30</option>
                        <option value="+33">+33</option>
                        <option value="+49">+49</option>
                        <option value="+39">+39</option>
                        <option value="+34">+34</option>
                        <option value="+351">+351</option>
                      </select>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={getLocText('placeholderPhone')}
                        className="phone-input"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="form-field full-width">
                  <label className="field-label">{t.message || "Message"}</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={getLocText('placeholderMessage')}
                    className="field-textarea"
                    rows="5"
                    required
                  />
                </div>

                <button type="submit" className="form-submit-btn">
                  {getLocText('sendBtn')}
                </button>
              </form>

              {/* Bank Account Details Section */}
              <div className="contact-bank-section" style={{ borderTop: '1px solid #e2e8f0', paddingTop: '36px', marginTop: '36px' }}>
                <h3 style={{ fontSize: '20px', fontWeight: '700', color: '#0c1e36', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#be1e2d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                    <line x1="2" y1="10" x2="22" y2="10" />
                    <path d="M6 14h.01M10 14h.01M14 14h.01" />
                  </svg>
                  {selectedLang.code === 'nl' ? 'Bankrekening Gegevens' : 'Bank Account Details'}
                </h3>
                <p style={{ fontSize: '14.5px', color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
                  {selectedLang.code === 'nl' 
                    ? 'Gebruik deze gegevens voor directe bankoverschrijvingen of factuurbetalingen.' 
                    : 'Use the following details for direct bank transfers or invoice payments.'}
                </p>
                
                <div style={{ 
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', 
                  border: '1.5px solid #e2e8f0', 
                  borderRadius: '20px', 
                  padding: '24px 30px',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.02)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Subtle watermarked bank emblem in background */}
                  <div style={{ position: 'absolute', right: '-10px', bottom: '-10px', opacity: 0.03, pointerEvents: 'none' }}>
                    <svg width="150" height="150" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M3 21h18M3 10h18M3 7l9-4 9 4M4 10v11M20 10v11M8 10v11M12 10v11M16 10v11" />
                    </svg>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px' }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                        {selectedLang.code === 'nl' ? 'Begunstigde' : 'Beneficiary Name'}
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#0c1e36' }}>Recent Inspiration</div>
                    </div>
                    
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                        {selectedLang.code === 'nl' ? 'Bank Naam' : 'Bank Name'}
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', color: '#0c1e36' }}>
                        ING BANK N.V.
                      </div>
                    </div>
                  </div>

                  <div style={{ borderTop: '1px dashed #e2e8f0', paddingTop: '16px' }}>
                    <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                      IBAN
                    </div>
                    <div style={{ fontSize: '18px', fontWeight: '800', color: '#be1e2d', letterSpacing: '0.5px', fontFamily: 'monospace' }}>
                      NL46 INGB 0119 4676 90
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', borderTop: '1px dashed #e2e8f0', paddingTop: '16px' }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                        BIC / SWIFT CODE
                      </div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: '#0c1e36', fontFamily: 'monospace' }}>INGBNL2AXXX</div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                        {selectedLang.code === 'nl' ? 'Filiaal' : 'Branch'}
                      </div>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#475569', lineHeight: '1.4' }}>
                        BIJLMERDREEF 106 AMSTERDAM<br />
                        1102CT Netherlands
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Info Sidebar Card */}
            <div className="contact-right-sidebar">
              <h3 className="sidebar-title">{getLocText('sidebarTitle')}</h3>
              <p className="sidebar-paragraph">
                {getLocText('sidebarDesc')}
              </p>

              <div className="sidebar-info-list">
                {/* Item 1 */}
                <div className="sidebar-info-item">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </div>
                  <div className="info-text-group">
                    <span className="info-label">Hotline:</span>
                    <span className="info-value">+31-687748358</span>
                  </div>
                </div>

                {/* Item 2 */}
                <div className="sidebar-info-item">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                    </svg>
                  </div>
                  <div className="info-text-group">
                    <span className="info-label">SMS / Whatsapp:</span>
                    <span className="info-value">+31-687748358</span>
                  </div>
                </div>

                {/* Item 3 */}
                <div className="sidebar-info-item">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </div>
                  <div className="info-text-group">
                    <span className="info-label">Email:</span>
                    <span className="info-value">smarttelecom.nl@gmail.com</span>
                  </div>
                </div>

                {/* Item 4 - Company Name */}
                <div className="sidebar-info-item">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                  </div>
                  <div className="info-text-group">
                    <span className="info-label">COMPANY NAME:</span>
                    <span className="info-value">Recent inspiration</span>
                  </div>
                </div>

                {/* Item 5 - KVK */}
                <div className="sidebar-info-item">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <line x1="9" y1="9" x2="15" y2="9" />
                      <line x1="9" y1="13" x2="15" y2="13" />
                      <line x1="9" y1="17" x2="11" y2="17" />
                    </svg>
                  </div>
                  <div className="info-text-group">
                    <span className="info-label">KVK:</span>
                    <span className="info-value">42091099</span>
                  </div>
                </div>

                {/* Item 6 - VAT ID */}
                <div className="sidebar-info-item">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                      <line x1="7" y1="7" x2="7.01" y2="7" />
                    </svg>
                  </div>
                  <div className="info-text-group">
                    <span className="info-label">VAT ID:</span>
                    <span className="info-value">NL005488135B34</span>
                  </div>
                </div>

                {/* Item 7 - Address */}
                <div className="sidebar-info-item">
                  <div className="info-icon-wrapper">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <div className="info-text-group">
                    <span className="info-label">ADDRESS:</span>
                    <a
                      href="https://maps.app.goo.gl/AE18QqsHPsFWtS8a9"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="info-value"
                      style={{ lineHeight: '1.4', color: 'inherit', textDecoration: 'none' }}
                    >
                      Smart Telecom Store<br />
                      Zuylesteinstraat 21, Almere<br />
                      1333XZ Netherlands
                    </a>
                  </div>
                </div>
              </div>

              <div className="sidebar-divider"></div>

              <div className="social-connect-block">
                <span className="connect-label">{getLocText('connectWithUs')}</span>
                <div className="social-links-row">
                  <a href="#facebook" aria-label="Facebook" className="social-link-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                    </svg>
                  </a>
                  <a href="#instagram" aria-label="Instagram" className="social-link-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                    </svg>
                  </a>
                  <a href="#snapchat" aria-label="Snapchat" className="social-link-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2c-3.8 0-7 3.2-7 7 0 2 .5 3.5 1.5 5-2 2.5-3 5.5-3 8h17c0-2.5-1-5.5-3-8 1-1.5 1.5-3 1.5-5 0-3.8-3.2-7-7-7z" />
                    </svg>
                  </a>
                  <a href="#tiktok" aria-label="TikTok" className="social-link-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 12a4 4 0 1 0 4 4V4h5v4h-3v8a4 4 0 0 1-6 3.5" />
                    </svg>
                  </a>
                  <a href="#twitter" aria-label="Twitter" className="social-link-item">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z" />
                    </svg>
                  </a>
                </div>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* 3. Google Maps Section — Smart Telecom Store (maps.app.goo.gl/AE18QqsHPsFWtS8a9) */}
      <section className="contact-map-section">
        <div className="contact-map-inner">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2435.035!2d5.266939!3d52.3879157!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c617e71c77098d%3A0x84795a8d7424005b!2sSmart%20Telecom%20Store!5e0!3m2!1sen!2snl!4v1710000000000!5m2!1sen!2snl"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Smart Telecom Store Almere Map"
          ></iframe>
        </div>
      </section>
    </div>
  );
};

export default ContactUs;
